import { array, bool, number, object, string } from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { useCellMeasurements } from "Boards";
import API from "Boards/API";
import AddColumn from "Boards/AddColumn";
import { BoardContext } from "Boards/BoardContext";
import ColumnMoreMenu from "Boards/ColumnMoreMenu";
import SortColumn from "Boards/SortColumn";
import useGroupOrSignupID from "Boards/useGroupOrSignupID";

import { TitleCellWrapper } from "./";
import { CELL_TYPES, getCellType } from "./Dynamic";

TitleRowCell.propTypes = {
  columnID: number,
  columnTitle: string,
  columnType: number,
  index: number,
  groupID: number,
  orderBy: number,
  isShared: bool,
  canEdit: bool,
  canEditHeader: bool,
  canFilter: bool,
  canDelete: bool,
  canAddColumns: bool,
  relatedSignups: array,
  style: object,
};

export function TitleRowCell({
  columnID,
  columnTitle,
  columnType,
  index,
  groupID,
  orderBy,
  isShared,
  canEdit,
  canEditHeader,
  canFilter,
  canDelete,
  canAddColumns,
  relatedSignups,
  style = {},
}) {
  const titleInputRef = useRef();

  const [{ boardID, isShared: boardIsShared }, dispatch] =
    useContext(BoardContext);
  const groupOrSignupID = useGroupOrSignupID();
  const [title, setTitle] = useState(columnTitle);
  const [editMode, setEditMode] = useState(false);

  const updateTitle = (e) => setTitle(e.target.value);

  const enterEditMode = () => {
    if (!canEditHeader) return;

    setEditMode(true);
  };

  useEffect(() => {
    if (!(editMode && titleInputRef.current instanceof HTMLInputElement))
      return;

    titleInputRef.current.focus();
  }, [editMode]);

  const handleBlur = () => {
    setEditMode(false);

    if (columnTitle === title) return; // don't save if value hasn't changed

    remeasureCell(title);

    API.renameColumn(boardID, {
      columnID,
      columnName: title,
      ...groupOrSignupID,
    })
      .post()
      .then(({ title: updatedTitle, columns }) => {
        setTitle(updatedTitle);
        updateFilterPanelOnNewColumnTitle(columnID, updatedTitle);
        dispatch({ type: "Update Columns", payload: columns });
      })
      .catch(() => {
        // revert optimistic update
        setTitle(columnTitle);
      });
  };

  const updateFilterPanelOnNewColumnTitle = (columnID, newTitle) => {
    API.getFilters({
      boardID,
      groupID,
    })
      .post()
      .then(({ filterInfo }) => {
        if (!window.fnReactBus?.filtersPanel) return;

        window.fnReactBus.filtersPanel.updateSheetsFilter(
          filterInfo,
          columnID,
          newTitle
        );
      });
  };

  const { width, MeasureCell, remeasureCell } = useCellMeasurements({
    columnID,
    columnType,
  });

  const showSignupIcon = isShared && relatedSignups?.length > 0;
  const showSharedIcon = isShared && !relatedSignups?.length;

  return (
    <>
      <TitleCellWrapper style={{ ...style, width }}>
        {canAddColumns ? <AddColumn orderBy={orderBy} /> : null}

        <TitleRowInside>
          {SORTABLE_CELL_TYPES.has(getCellType(columnType)) && (
            <SortColumn columnID={columnID} columnType={columnType} />
          )}
          {editMode ? (
            <Input
              type="text"
              value={title}
              onChange={updateTitle}
              maxLength="30"
              onBlur={handleBlur}
              ref={titleInputRef}
            />
          ) : (
            <Display
              onClick={enterEditMode}
              className={!canEditHeader ? "disabled" : ""}
            >
              <Title>{title}</Title>
              {showSignupIcon && (
                <SharedIcon className="far fa-clipboard-check" />
              )}
              {showSharedIcon && <SharedIcon className="far fa-share" />}
            </Display>
          )}
          <ColumnMoreMenu
            columnID={columnID}
            columnTitle={columnTitle}
            columnType={columnType}
            columnIndex={index}
            isShared={isShared}
            canEdit={canEdit}
            canFilter={canFilter}
            canDelete={canDelete}
            relatedSignups={relatedSignups}
          />
        </TitleRowInside>

        {canAddColumns ? (
          <AddColumn orderBy={orderBy + 1} isAfterColumn={true} />
        ) : null}
      </TitleCellWrapper>
      <MeasureCell>
        <MeasureStyling $isShared={isShared && !boardIsShared} $showingSignupIcon={showSignupIcon}>
          {title}
        </MeasureStyling>
      </MeasureCell>
    </>
  );
}

const SORTABLE_CELL_TYPES = new Set([
  CELL_TYPES.BOOL,
  CELL_TYPES.TEXT,
  CELL_TYPES.NUMBER,
  CELL_TYPES.DATE,
  CELL_TYPES.DATE_TIME,
  CELL_TYPES.SELECT_LIST,
  CELL_TYPES.SIGNUP_ITEMS,
]);

const TitleRowInside = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 100%;
`;

const sharedTitleStyles = `
  text-align: center;
  text-transform: uppercase;
  color: #384a5d;
  font-size: 13px;
  font-weight: 400;
`;

const Input = styled.input`
  ${sharedTitleStyles}
  white-space: nowrap;
  padding: 5px;
  appearance: none;
  border: 1px solid rgba(0, 0, 0, 0);
  background: none;
  margin: 0 6px;
  width: calc(100% - 12px);
  box-sizing: border-box;
  outline: none;
  border-radius: 6px;
  transition: border 0.2s, background 0.2s;

  &:focus {
    background: #fff;
    border: 1px solid #278dc1;
  }
`;

const Display = styled.div`
  ${sharedTitleStyles}
  padding: 0 5px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
  max-height: 32px;
  overflow: hidden;

  &.disabled {
    cursor: default;
  }
`;

const Title = styled.div``;
const SharedIcon = styled.div`
  font-size: 9px;
  width: 14px;
  text-align: center;
  flex-grow: 0;
  flex-shrink: 0;
  align-self: center;
`;

const MeasureStyling = styled.div`
  text-transform: uppercase;
  font-size: 13px;
  font-weight: 400;
  white-space: nowrap;
  padding: 0 6px;
  padding-right: ${({ $isShared, $showingSignupIcon }) =>
    $isShared || $showingSignupIcon ? "36px" : "6px"};
`;